import { useState, type ReactNode } from 'react';
import { Button, Text, ledgerContainerClass } from '@billda/design-system';

// Harness-only chrome: a resizable frame that owns the CSS container the
// ledger's collapse point queries. Real screens own their own container.

const FRAME_WIDTHS = [
  { key: 'desktop', label: 'Desktop', width: '100%' },
  { key: 'tablet', label: '744px', width: 744 },
  { key: 'phone', label: '390px', width: 390 },
] as const;

type FrameKey = (typeof FRAME_WIDTHS)[number]['key'];

interface ScreenFrameProps {
  title: string;
  subtitle?: string;
  /** CSS container name the ledger inside queries. */
  container: string;
  children: ReactNode;
}

export function ScreenFrame({
  title,
  subtitle,
  container,
  children,
}: ScreenFrameProps) {
  const [frame, setFrame] = useState<FrameKey>('desktop');
  const width = FRAME_WIDTHS.find(f => f.key === frame)?.width ?? '100%';

  return (
    <div className="mb-12">
      <div className="mb-3 flex flex-wrap items-center gap-4">
        <div className="min-w-0">
          <Text as="div" variant="body1" className="font-semibold">
            {title}
          </Text>
          {subtitle && (
            <Text variant="caption" color="secondary">
              {subtitle}
            </Text>
          )}
        </div>
        <div className="flex-1" />
        <div className="flex gap-2">
          {FRAME_WIDTHS.map(f => (
            <Button
              key={f.key}
              type="button"
              size="sm"
              variant={frame === f.key ? 'primary' : 'outline'}
              className="px-2.5 py-0.5"
              onClick={() => setFrame(f.key)}
            >
              <Text as="span" variant="caption">
                {f.label}
              </Text>
            </Button>
          ))}
        </div>
      </div>
      <div
        className={`max-w-full overflow-hidden rounded-xl border border-border-default bg-surface-default ${ledgerContainerClass(container)}`}
        style={{ width }}
      >
        {children}
      </div>
    </div>
  );
}
